import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, Navigate, Link } from 'react-router-dom';
import { UserCircle, History, CreditCard } from 'lucide-react';
import AuthButton from './AuthButton';
import { useAuth } from '../hooks/useAuth';
import { StripeProvider } from '../contexts/StripeContext';
import Footer from './Footer';
import logo from '../assets/images/logo-300x300.png';

export default function Layout() {
  const { user, loading } = useAuth();
  const [forceLoaded, setForceLoaded] = useState(false);
  const location = useLocation();

  // Define public routes that don't require authentication
  const publicRoutes = ['/', '/privacy'];

  // If loading takes too long, we might want to force it to complete
  useEffect(() => {
    if (loading && !forceLoaded) {
      const timeout = setTimeout(() => {
        // Force loading to complete after a reasonable time
        console.warn('Loading timeout reached in Layout, forcing completion');
        setForceLoaded(true);
      }, 5000); // 5 seconds timeout
      
      return () => clearTimeout(timeout);
    }
  }, [loading, forceLoaded]);

  // Show loading state
  if (loading && !forceLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Redirect authenticated users to upload page if they're on the landing page
  if (user && location.pathname === '/') {
    return <Navigate to="/upload" replace />;
  }

  // Only redirect unauthenticated users if they're not on a public route
  if (!user && !publicRoutes.includes(location.pathname)) {
    return <Navigate to="/" replace />;
  }

  return (
    <StripeProvider>
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <nav className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16">
              <div className="flex">
                <div className="flex-shrink-0 flex items-center space-x-2">
                  <img src={logo} alt="PostNudge Logo" className="h-8 w-8" />
                  <Link to={user ? '/upload' : '/'} className="text-xl font-bold">
                    PostNudge
                  </Link>
                </div>
              </div>
              {user && (
                <div className="flex items-center space-x-4">
                  <Link
                    to="/history"
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                      location.pathname === '/history'
                        ? 'bg-gray-100 text-gray-900'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <History className="w-5 h-5" />
                    <span>History</span>
                  </Link>
                  <Link
                    to="/billing"
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                      location.pathname === '/billing'
                        ? 'bg-gray-100 text-gray-900'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <CreditCard className="w-5 h-5" />
                    <span>Billing</span>
                  </Link>
                  <Link
                    to="/profile"
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                      location.pathname === '/profile'
                        ? 'bg-gray-100 text-gray-900'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <UserCircle className="w-5 h-5" />
                    <span>Profile</span>
                  </Link>
                  <AuthButton 
                    isAuthenticated={true}
                    onSignOut={() => {
                      // No need to do anything here as useAuth will handle the state
                    }}
                  />
                </div>
              )}
              {!user && (
                <div className="flex items-center">
                  <AuthButton 
                    isAuthenticated={false}
                    onSignOut={() => {}}
                  />
                </div>
              )}
            </div>
          </div>
        </nav>

        <main className="flex-grow">
          <Outlet />
        </main>
        <Footer />
      </div>
    </StripeProvider>
  );
}
